/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  USE FACTION THEME - Faction-based personalization                        ║
 * ║  Changes accent colors based on player's chosen faction                   ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useEffect } from 'react';

// Faction color themes matching the 8 factions
const FACTION_THEMES: Record<number, {
  accent: string;
  accentRGB: string;
  secondary: string;
  secondaryRGB: string;
  name: string;
}> = {
  0: { // No faction - default cyberpunk
    accent: '#00ffff',
    accentRGB: '0, 255, 255',
    secondary: '#ff0080',
    secondaryRGB: '255, 0, 128',
    name: 'default',
  },
  1: { // Raven - Deep purple/midnight
    accent: '#8b5cf6',
    accentRGB: '139, 92, 246',
    secondary: '#c084fc',
    secondaryRGB: '192, 132, 252',
    name: 'raven',
  },
  2: { // Ape - Jungle green/gold
    accent: '#22c55e',
    accentRGB: '34, 197, 94',
    secondary: '#fbbf24',
    secondaryRGB: '251, 191, 36',
    name: 'ape',
  },
  3: { // Moth - Ethereal amber/cream
    accent: '#f59e0b',
    accentRGB: '245, 158, 11',
    secondary: '#fcd34d',
    secondaryRGB: '252, 211, 77',
    name: 'moth',
  },
  4: { // Viper - Toxic green/acid
    accent: '#84cc16',
    accentRGB: '132, 204, 22',
    secondary: '#22d3ee',
    secondaryRGB: '34, 211, 238',
    name: 'viper',
  },
  5: { // Rabbit - Electric pink/neon
    accent: '#ec4899',
    accentRGB: '236, 72, 153',
    secondary: '#f472b6',
    secondaryRGB: '244, 114, 182',
    name: 'rabbit',
  },
  6: { // Wolf - Ice blue/silver
    accent: '#3b82f6',
    accentRGB: '59, 130, 246',
    secondary: '#93c5fd',
    secondaryRGB: '147, 197, 253',
    name: 'wolf',
  },
  7: { // Fox - Warm orange/amber
    accent: '#f97316',
    accentRGB: '249, 115, 22',
    secondary: '#fb923c',
    secondaryRGB: '251, 146, 60',
    name: 'fox',
  },
  8: { // Owl - Golden/cosmic
    accent: '#eab308',
    accentRGB: '234, 179, 8',
    secondary: '#a855f7',
    secondaryRGB: '168, 85, 247',
    name: 'owl',
  },
};

/**
 * Applies faction-specific CSS custom properties to the document root.
 * Components can use var(--faction-accent) to auto-adapt to the player's faction.
 */
export function useFactionTheme(factionId: number) {
  useEffect(() => {
    const theme = FACTION_THEMES[factionId] || FACTION_THEMES[0];
    const root = document.documentElement;

    root.style.setProperty('--faction-accent', theme.accent);
    root.style.setProperty('--faction-accent-rgb', theme.accentRGB);
    root.style.setProperty('--faction-secondary', theme.secondary);
    root.style.setProperty('--faction-secondary-rgb', theme.secondaryRGB);
    root.dataset.faction = theme.name;

    return () => {
      // Reset to default on unmount
      const defaultTheme = FACTION_THEMES[0];
      root.style.setProperty('--faction-accent', defaultTheme.accent);
      root.style.setProperty('--faction-accent-rgb', defaultTheme.accentRGB);
      root.style.setProperty('--faction-secondary', defaultTheme.secondary);
      root.style.setProperty('--faction-secondary-rgb', defaultTheme.secondaryRGB);
      delete root.dataset.faction;
    };
  }, [factionId]);
}

export { FACTION_THEMES };
